import React from 'react';
import { Pizza, Coffee, Apple, Salad, Beef } from 'lucide-react';
import { ScheduledMeal } from '../types';

interface MealTypeSummaryProps {
  filteredMeals: ScheduledMeal[];
}

const MealTypeSummary: React.FC<MealTypeSummaryProps> = ({ filteredMeals }) => {
  // Group meals by type and count quantities
  const mealsByType: Record<string, { count: number; quantity: number }> = {};
  
  filteredMeals.forEach(meal => {
    if (!mealsByType[meal.type]) {
      mealsByType[meal.type] = { count: 0, quantity: 0 };
    }
    mealsByType[meal.type].count += 1;
    mealsByType[meal.type].quantity += meal.qty;
  });
  
  const getMealIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'main snack':
        return <Pizza className="h-6 w-6 text-amber-500" />;
      case 'snack':
        return <Coffee className="h-6 w-6 text-amber-500" />;
      case 'fruit':
        return <Apple className="h-6 w-6 text-green-500" />;
      case 'salad':
        return <Salad className="h-6 w-6 text-green-500" />;
      case 'drink':
        return <Coffee className="h-6 w-6 text-blue-500" />;
      default:
        return <Beef className="h-6 w-6 text-red-500" />;
    }
  };
  
  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="p-4 bg-indigo-50">
        <h2 className="text-lg font-medium text-indigo-800">Meal Summary</h2>
      </div>
      
      <div className="p-4">
        <div className="space-y-4">
          {Object.keys(mealsByType).length === 0 ? (
            <p className="text-center py-4 text-gray-500">No meals to summarize</p>
          ) : (
            Object.entries(mealsByType).map(([type, data]) => (
              <div 
                key={type}
                className="flex items-center justify-between p-3 border rounded-lg"
              >
                <div className="flex items-center">
                  <div className="p-2 rounded-lg bg-gray-100">
                    {getMealIcon(type)}
                  </div>
                  <div className="ml-3">
                    <h3 className="font-medium">{type}</h3>
                    <p className="text-sm text-gray-600">{data.count} orders</p>
                  </div>
                </div>
                <div className="text-xl font-semibold">
                  {data.quantity}
                </div>
              </div>
            ))
          )}
        </div>
        
        <div className="mt-6">
          <div className="flex justify-between items-center p-4 bg-gray-100 rounded-lg">
            <span className="font-medium">Total Items</span>
            <span className="text-xl font-semibold">
              {Object.values(mealsByType).reduce((sum, data) => sum + data.quantity, 0)}
            </span>
          </div>
        </div>
        
        <div className="mt-4">
          <div className="border-t pt-4">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Prepared</span>
              <span className="font-medium">
                {filteredMeals.filter(m => m.prepared).length} of {filteredMeals.length}
              </span>
            </div>
            <div className="mt-2 bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-emerald-600 h-2.5 rounded-full" 
                style={{ 
                  width: `${(filteredMeals.filter(m => m.prepared).length / Math.max(filteredMeals.length, 1)) * 100}%` 
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MealTypeSummary;